/**
 * AdminDashboard - [Phase 2a.2-C §5.2]
 *
 * Five sections:
 *   A. Active Leagues - list with force-action buttons (Reset / Delete / Advance Week)
 *   B. Users - read-only list with role filter
 *   C. Voice Health - llm/template counts over 24h + per-type breakdown (R-3)
 *   D. Server Health - DB + LLM keys + ElevenLabs cached timestamp (R-5)
 *   E. Recent Admin Actions - last 50 audit events
 *
 * All force-actions POST to /api/simulator/* per D.5/R10. This page is
 * read-side + thin wrapper around those existing mutation endpoints.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import {
  ArrowLeft, RefreshCw, Trash2, FastForward, Shield, Activity,
  Users as UsersIcon, Server, ClipboardList, AlertTriangle, Check,
} from "lucide-react";
import { useAuth, authHeaders } from "../contexts/AuthContext";
import ConfirmModal from "../components/common/ConfirmModal";
// [Session C-A CP-9] 7-category manual update panel.
import ManualUpdatePanel from "../components/admin/ManualUpdatePanel";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const StatCard = ({ label, value, accent, dataTestId }) => (
  <div
    data-testid={dataTestId}
    className="bg-[#1C1C1C] border border-[#252525] rounded-xl px-4 py-3"
  >
    <p className="font-body text-[11px] uppercase tracking-wider text-[#A0A0A0]">
      {label}
    </p>
    <p
      className={`font-display font-black text-xl mt-1 ${
        accent || "text-[#F5F5F5]"
      }`}
    >
      {value}
    </p>
  </div>
);

export default function AdminDashboard() {
  const { user } = useAuth();
  const nav = useNavigate();

  const [leagues, setLeagues] = useState([]);
  const [users, setUsers] = useState([]);
  const [voice, setVoice] = useState(null);
  const [voiceByType, setVoiceByType] = useState([]);
  const [server, setServer] = useState(null);
  const [auditEvents, setAuditEvents] = useState([]);
  // [Session C-A CP-8] Odds API credit ledger for the small numeric card.
  const [oddsCredits, setOddsCredits] = useState(null);
  const [busy, setBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const [actionFlash, setActionFlash] = useState("");
  const [actionError, setActionError] = useState("");

  // [TD-89 v2 Verdicts 6.A + 12.A + 13.A] Bulk-delete + filters state.
  const [selectedLeagueIds, setSelectedLeagueIds] = useState(() => new Set());
  const [filterStatus, setFilterStatus] = useState("");      // "" === All
  const [filterAge, setFilterAge] = useState("");            // "" | "7" | "30" | "90"
  const [filterMgrs, setFilterMgrs] = useState("");          // "" | "solo" | "few" | "full"
  const [bulkConfirmOpen, setBulkConfirmOpen] = useState(false);
  const [legacyDelete, setLegacyDelete] = useState(null);    // {leagueId, name} | null

  const refresh = useCallback(async () => {
    setLoadError("");
    try {
      const headers = authHeaders();
      const [lg, us, vh, vht, sh, ae, oc] = await Promise.all([
        axios.get(`${API}/admin/leagues?limit=50`, { headers }),
        axios.get(`${API}/admin/users?limit=50`, { headers }),
        axios.get(`${API}/admin/voice-health?hours=24`, { headers }),
        axios.get(`${API}/admin/voice-health/by-type?days=7`, { headers }),
        axios.get(`${API}/admin/server-health`, { headers }),
        axios.get(`${API}/admin/audit-events?limit=50`, { headers }),
        // [CP-8] Odds API credit-ledger endpoint. Fail-soft if the
        // backend hasn't been redeployed with the CP-8 route yet.
        axios.get(`${API}/admin/jobs/odds/credits`, { headers }).catch(() => ({ data: null })),
      ]);
      setLeagues(lg.data.items || []);
      setUsers(us.data.items || []);
      setVoice(vh.data);
      setVoiceByType(vht.data.items || []);
      setServer(sh.data);
      setAuditEvents(ae.data.items || []);
      setOddsCredits(oc.data);
    } catch (e) {
      setLoadError(e?.response?.data?.detail || "Failed to load admin data");
    }
  }, []);

  useEffect(() => {
    if (user && user.role !== "admin") nav("/dashboard");
    else refresh();
  }, [user, nav, refresh]);

  const flash = (msg) => {
    setActionError("");
    setActionFlash(msg);
    setTimeout(() => setActionFlash(""), 2500);
  };

  const showError = (err, fallback) => {
    setActionFlash("");
    const detail = err?.response?.data?.detail || fallback;
    // eslint-disable-next-line no-console
    console.warn("[admin]", fallback, err);
    setActionError(`${fallback}: ${detail}`);
  };

  const forceReset = async (leagueId) => {
    if (!window.confirm("Force-reset this league to LOBBY (week 1)?")) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/simulator/${leagueId}/reset-to-week/1`,
        {},
        { headers: authHeaders() },
      );
      flash("League reset to LOBBY");
      await refresh();
    } catch (e) {
      showError(e, "Reset failed");
    } finally {
      setBusy(false);
    }
  };

  const forceDelete = async (leagueId, name) => {
    // [TD-89 v2 Verdict 9.A] Repointed from /api/simulator/{id} to
    // /api/leagues/{id} so admin uses the canonical cascade chain.
    // Also wrapped in ConfirmModal (Verdict 7.A) via setLegacyDelete state.
    setLegacyDelete({ leagueId, name });
  };

  const _doLegacyForceDelete = async ({ leagueId, name }) => {
    setBusy(true);
    try {
      await axios.delete(`${API}/leagues/${leagueId}`, { headers: authHeaders() });
      flash(`Deleted ${name}`);
      await refresh();
    } catch (e) {
      showError(e, "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  const forceAdvance = async (leagueId) => {
    if (!window.confirm("Force-advance this league by one week?")) return;
    setBusy(true);
    try {
      await axios.post(
        `${API}/simulator/${leagueId}/advance-week`,
        {},
        { headers: authHeaders() },
      );
      flash("Week advanced");
      await refresh();
    } catch (e) {
      showError(e, "Advance failed");
    } finally {
      setBusy(false);
    }
  };

  if (!user || user.role !== "admin") {
    return null;
  }

  // [TD-89 v2 Verdict 12.A] Client-side filter pipeline.
  const _now = Date.now();
  const _filteredLeagues = leagues.filter((l) => {
    if (filterStatus && l.status !== filterStatus) return false;
    if (filterAge) {
      const days = Number(filterAge);
      const created = l.created_at ? new Date(l.created_at).getTime() : _now;
      if (_now - created < days * 86400 * 1000) return false;
    }
    if (filterMgrs) {
      const n = Number(l.team_count || 0);
      if (filterMgrs === "solo" && n > 1) return false;
      if (filterMgrs === "few" && (n < 2 || n > 3)) return false;
      if (filterMgrs === "full" && n < 4) return false;
    }
    return true;
  });

  const _bulkDelete = async () => {
    setBusy(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/leagues/bulk-delete`,
        { league_ids: Array.from(selectedLeagueIds) },
        { headers: authHeaders() },
      );
      flash(`Deleted ${data.deleted?.length || 0} · failed ${data.failed?.length || 0}`);
      // [HF-TD89 Bug 2/3 follow-up] Surface the first failure reason
      // inline so the operator sees actionable info rather than a generic
      // "failed N" count. Avoids a second click to inspect per-league
      // failure causes during smoke / production triage.
      if (Array.isArray(data.failed) && data.failed.length > 0) {
        const first = data.failed[0];
        const reason = (first?.reason || "unknown").toString().slice(0, 120);
        showError(
          { response: { data: { detail: `Some leagues couldn't be deleted. First failure: ${reason}` } } },
          `Partial failure: ${data.failed.length} of ${data.failed.length + (data.deleted?.length || 0)} leagues failed`,
        );
      }
      setSelectedLeagueIds(new Set());
      await refresh();
    } catch (e) {
      showError(e, "Bulk delete failed");
    } finally {
      setBusy(false);
      setBulkConfirmOpen(false);
    }
  };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-[#111111] text-[#F5F5F5]">
      {/* Header */}
      <header className="bg-[#1C1C1C] border-b border-[#252525] h-16 flex items-center px-4 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto w-full flex items-center gap-3">
          <button
            data-testid="admin-back-to-dashboard"
            onClick={() => nav("/dashboard")}
            className="text-[#A0A0A0] hover:text-[#FF6B00]"
          >
            <ArrowLeft size={20} />
          </button>
          <Shield size={18} className="text-[#FF6B00]" />
          <span className="font-display font-black uppercase text-base">
            Admin Dashboard
          </span>
          <button
            data-testid="admin-refresh-btn"
            onClick={refresh}
            disabled={busy}
            className="ml-auto text-[#A0A0A0] hover:text-[#FF6B00] disabled:opacity-40"
          >
            <RefreshCw size={18} />
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-6 space-y-8">
        {loadError && (
          <div
            data-testid="admin-error"
            className="bg-[#FF4444]/10 border border-[#FF4444]/30 rounded-lg px-4 py-3 text-[#FF4444] font-body text-sm"
          >
            {loadError}
          </div>
        )}
        {actionError && (
          <div
            data-testid="admin-action-error"
            className="bg-[#FF4444]/10 border border-[#FF4444]/30 rounded-lg px-4 py-3 text-[#FF4444] font-body text-sm flex items-center justify-between gap-2"
          >
            <span className="flex items-center gap-2">
              <AlertTriangle size={14} /> {actionError}
            </span>
            <button
              type="button"
              onClick={() => setActionError("")}
              className="text-[#FF4444]/70 hover:text-[#FF4444]"
              data-testid="admin-action-error-dismiss"
              aria-label="Dismiss"
            >
              ×
            </button>
          </div>
        )}
        {actionFlash && (
          <div
            data-testid="admin-flash"
            className="bg-[#FF6B00]/10 border border-[#FF6B00]/30 rounded-lg px-4 py-3 text-[#FF6B00] font-body text-sm flex items-center gap-2"
          >
            <Check size={14} /> {actionFlash}
          </div>
        )}

        {/* Summary cards */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <StatCard
            label="Active Leagues"
            value={leagues.length}
            dataTestId="admin-stat-leagues"
          />
          <StatCard
            label="Users"
            value={users.length}
            dataTestId="admin-stat-users"
          />
          <StatCard
            label="Template %  (24h)"
            value={voice ? `${voice.template_pct}%` : "-"}
            accent={voice?.red_flag ? "text-[#FF4444]" : "text-[#F5F5F5]"}
            dataTestId="admin-stat-voice-template-pct"
          />
          <StatCard
            label="DB"
            value={server?.database_ok ? "OK" : "DOWN"}
            accent={server?.database_ok ? "text-[#00C853]" : "text-[#FF4444]"}
            dataTestId="admin-stat-db"          />
        </section>

        {/* [Session C-A CP-8] Odds API credits row - small numeric card
            per founder's CP-7 suggestion. Only renders when the
            /admin/jobs/odds/credits endpoint is reachable. */}
        {oddsCredits && (
          <section
            data-testid="admin-section-odds-credits"
            className="grid grid-cols-2 md:grid-cols-4 gap-4"
          >
            <StatCard
              label="Odds API - used (mo.)"
              value={oddsCredits.credits_used_total ?? "-"}
              accent={
                oddsCredits.credits_used_total > (oddsCredits.credits_limit * 0.85)
                  ? "text-[#FF4444]"
                  : oddsCredits.credits_used_total > (oddsCredits.credits_limit * 0.5)
                  ? "text-[#FFB020]"
                  : "text-[#F5F5F5]"
              }
              dataTestId="admin-stat-odds-credits-used"
            />
            <StatCard
              label="Odds API - remaining"
              value={oddsCredits.credits_remaining ?? "-"}
              accent="text-[#00C853]"
              dataTestId="admin-stat-odds-credits-remaining"
            />
            <StatCard
              label="Odds API - pulls (mo.)"
              value={oddsCredits.pulls_this_month ?? 0}
              dataTestId="admin-stat-odds-credits-pulls"
            />
            <StatCard
              label="Odds API - quota"
              value={`${oddsCredits.credits_limit ?? "-"}`}
              dataTestId="admin-stat-odds-credits-limit"
            />
          </section>
        )}

        {/* [Session C-A CP-9] Manual Update Panel (7-category job sequencer) */}
        <ManualUpdatePanel />

        {/* D. Server Health */}
        <section
          data-testid="admin-section-server-health"
          className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5"
        >
          <h2 className="font-display font-bold text-base uppercase flex items-center gap-2 mb-3">
            <Server size={14} className="text-[#FF6B00]" /> Server Health
          </h2>
          {server && (
            <dl className="grid md:grid-cols-2 gap-x-8 gap-y-1 font-body text-sm">
              <Pair label="Database" v={server.database_ok ? "OK" : "DOWN"} ok={server.database_ok} />
              <Pair label="Claude LLM key" v={server.claude_llm_key_set ? "set" : "missing"} ok={server.claude_llm_key_set} />
              <Pair label="Emergent LLM key" v={server.emergent_llm_key_set ? "set" : "missing"} ok={server.emergent_llm_key_set} />
              <Pair label="ElevenLabs key" v={server.elevenlabs_key_set ? "set" : "missing"} ok={server.elevenlabs_key_set} />
              <Pair
                label="ElevenLabs last success"
                v={server.elevenlabs_last_success_at || "no recent call"}
                ok={!!server.elevenlabs_last_success_at}
              />
              <Pair label="Sentry DSN" v={server.sentry_dsn_set ? "set" : "unset"} ok={server.sentry_dsn_set} />
              <Pair label="Resend API key" v={server.resend_api_key_set ? "set" : "unset"} ok={server.resend_api_key_set} />
              <Pair label="Deploy mode" v={server.deploy_mode} ok={true} />
            </dl>
          )}
        </section>

        {/* C. Voice Health (R-3) */}
        <section
          data-testid="admin-section-voice-health"
          className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5"
        >
          <h2 className="font-display font-bold text-base uppercase flex items-center gap-2 mb-3">
            <Activity size={14} className="text-[#FF6B00]" /> Voice Health (Adam)
          </h2>
          {voice && (
            <div className="space-y-4">
              <div className="grid grid-cols-3 gap-4">
                <StatCard label="LLM (24h)" value={voice.llm_count} accent="text-[#00C853]" dataTestId="admin-voice-llm" />
                <StatCard label="Template (24h)" value={voice.template_count} accent={voice.template_count > 0 ? "text-[#FFB020]" : "text-[#F5F5F5]"} dataTestId="admin-voice-template" />
                <StatCard
                  label="Template %"
                  value={`${voice.template_pct}%`}
                  accent={voice.red_flag ? "text-[#FF4444]" : "text-[#F5F5F5]"}
                  dataTestId="admin-voice-pct"
                />
              </div>
              {voice.red_flag && (
                <div
                  data-testid="admin-voice-red-flag"
                  className="bg-[#FF4444]/10 border border-[#FF4444]/30 rounded-lg px-3 py-2 text-[#FF4444] font-body text-xs flex items-center gap-2"
                >
                  <AlertTriangle size={14} /> Template rate above 10% over 24h - LLM may be unreachable or cap-tuning regression.
                </div>
              )}
              {voiceByType.length > 0 && (
                <div>
                  <p className="font-display font-bold text-xs uppercase tracking-wider text-[#A0A0A0] mb-2">By type (7d)</p>
                  <table className="w-full text-sm font-body">
                    <thead>
                      <tr className="text-[#A0A0A0] text-[11px] uppercase tracking-wider">
                        <th className="text-left py-1">Type</th>
                        <th className="text-right py-1">LLM</th>
                        <th className="text-right py-1">Template</th>
                        <th className="text-right py-1">Template %</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-[#252525]">
                      {voiceByType.map((r) => (
                        <tr key={r.type} data-testid={`admin-voice-row-${r.type}`}>
                          <td className="py-1.5 font-mono text-xs">{r.type}</td>
                          <td className="py-1.5 text-right">{r.llm}</td>
                          <td className="py-1.5 text-right">{r.template}</td>
                          <td className={`py-1.5 text-right ${r.template_pct > 10 ? "text-[#FF4444] font-bold" : ""}`}>
                            {r.template_pct}%
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </section>

        {/* A. Active Leagues */}
        <section
          data-testid="admin-section-leagues"
          className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5"
        >
          <h2 className="font-display font-bold text-base uppercase flex items-center gap-2 mb-3">
            <ClipboardList size={14} className="text-[#FF6B00]" /> Active Leagues ({leagues.length})
          </h2>

          {/* [TD-89 v2 Verdict 12.A] Client-side filter row */}
          <div className="flex flex-wrap items-center gap-2 mb-3 text-xs">
            <select
              data-testid="admin-filter-status"
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="bg-[#111111] border border-[#252525] rounded px-2 py-1 text-[#F5F5F5]"
            >
              <option value="">All Statuses</option>
              {["SETUP","LOBBY","DRAFTING","PAUSED","SEASON_ACTIVE","SEASON_COMPLETE","ARCHIVED"].map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
            <select
              data-testid="admin-filter-age"
              value={filterAge}
              onChange={(e) => setFilterAge(e.target.value)}
              className="bg-[#111111] border border-[#252525] rounded px-2 py-1 text-[#F5F5F5]"
            >
              <option value="">Any Age</option>
              <option value="7">Older than 7d</option>
              <option value="30">Older than 30d</option>
              <option value="90">Older than 90d</option>
            </select>
            <select
              data-testid="admin-filter-managers"
              value={filterMgrs}
              onChange={(e) => setFilterMgrs(e.target.value)}
              className="bg-[#111111] border border-[#252525] rounded px-2 py-1 text-[#F5F5F5]"
            >
              <option value="">Any Size</option>
              <option value="solo">Just commissioner</option>
              <option value="few">Few (2-3)</option>
              <option value="full">Full (≥4)</option>
            </select>
            <div className="flex-1" />
            <button
              data-testid="admin-bulk-delete-btn"
              disabled={selectedLeagueIds.size === 0 || busy}
              onClick={() => setBulkConfirmOpen(true)}
              className="bg-[#E03030] hover:bg-[#F04040] disabled:opacity-30 disabled:cursor-not-allowed text-white font-display font-black uppercase tracking-wider text-xs px-3 py-1.5 rounded transition-all"
            >
              <Trash2 size={11} className="inline mr-1" /> Bulk Delete ({selectedLeagueIds.size})
            </button>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="text-[#A0A0A0] text-[11px] uppercase tracking-wider">
                  <th className="text-left py-1.5 w-8">
                    <input
                      data-testid="admin-bulk-select-all"
                      type="checkbox"
                      checked={selectedLeagueIds.size > 0 && _filteredLeagues.every(l => selectedLeagueIds.has(l.id))}
                      onChange={(e) => {
                        const next = new Set(selectedLeagueIds);
                        if (e.target.checked) _filteredLeagues.forEach(l => next.add(l.id));
                        else _filteredLeagues.forEach(l => next.delete(l.id));
                        setSelectedLeagueIds(next);
                      }}
                    />
                  </th>
                  <th className="text-left py-1.5">Name</th>
                  <th className="text-left py-1.5">Type</th>
                  <th className="text-left py-1.5">Status</th>
                  <th className="text-left py-1.5">Commish</th>
                  <th className="text-right py-1.5">Teams</th>
                  <th className="text-left py-1.5">Last Activity</th>
                  <th className="text-right py-1.5">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#252525]">
                {_filteredLeagues.map((l) => {
                  const hasSeason = !["SETUP", "LOBBY", "DRAFTING"].includes(l.status);
                  const checked = selectedLeagueIds.has(l.id);
                  return (
                  <tr key={l.id} data-testid={`admin-league-row-${l.id}`}>
                    <td className="py-1.5">
                      <input
                        data-testid={`admin-league-row-checkbox-${l.id}`}
                        type="checkbox"
                        checked={checked}
                        onChange={() => {
                          const next = new Set(selectedLeagueIds);
                          if (checked) next.delete(l.id); else next.add(l.id);
                          setSelectedLeagueIds(next);
                        }}
                      />
                    </td>
                    <td className="py-1.5 font-medium text-[#F5F5F5]">{l.name}</td>
                    <td className="py-1.5 font-mono text-[11px] text-[#A0A0A0]">{l.league_type}</td>
                    <td className="py-1.5">
                      <span className="inline-block px-2 py-0.5 bg-[#252525] rounded text-[11px] font-display font-bold uppercase tracking-wide">
                        {l.status}
                      </span>
                    </td>
                    <td className="py-1.5 text-[#A0A0A0] text-xs">{l.commissioner_email}</td>
                    <td className="py-1.5 text-right">{l.team_count}</td>
                    <td className="py-1.5 text-[#A0A0A0] text-[11px]">
                      {l.last_activity_at
                        ? new Date(l.last_activity_at).toLocaleString()
                        : "-"}
                    </td>
                    <td className="py-1.5 text-right space-x-1">
                      <button
                        title={hasSeason ? "Force Reset to LOBBY (week 1)" : "No season - reset not applicable"}
                        onClick={() => forceReset(l.id)}
                        disabled={busy || !hasSeason}
                        data-testid={`admin-force-reset-${l.id}`}
                        className="p-1.5 bg-[#252525] hover:bg-[#FFB020]/20 rounded text-[#A0A0A0] hover:text-[#FFB020] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <RefreshCw size={12} />
                      </button>
                      <button
                        title={hasSeason ? "Force Advance Week" : "No season - advance not applicable"}
                        onClick={() => forceAdvance(l.id)}
                        disabled={busy || !hasSeason}
                        data-testid={`admin-force-advance-${l.id}`}
                        className="p-1.5 bg-[#252525] hover:bg-[#FF6B00]/20 rounded text-[#A0A0A0] hover:text-[#FF6B00] transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <FastForward size={12} />
                      </button>
                      <button
                        title="Delete League"
                        onClick={() => forceDelete(l.id, l.name)}
                        disabled={busy}
                        data-testid={`admin-force-delete-${l.id}`}
                        className="p-1.5 bg-[#252525] hover:bg-[#FF4444]/20 rounded text-[#A0A0A0] hover:text-[#FF4444] transition-colors disabled:opacity-40"
                      >
                        <Trash2 size={12} />
                      </button>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </section>

        {/* B. Users */}
        <section
          data-testid="admin-section-users"
          className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5"
        >
          <h2 className="font-display font-bold text-base uppercase flex items-center gap-2 mb-3">
            <UsersIcon size={14} className="text-[#FF6B00]" /> Users ({users.length})
            <button
              type="button"
              data-testid="admin-link-user-management"
              onClick={() => nav("/admin/users")}
              className="ml-auto text-[10px] uppercase tracking-widest text-[#FF6B00] hover:text-[#FF8533] border border-[#FF6B00]/60 hover:border-[#FF6B00] rounded-sm px-2 py-0.5"
            >
              Manage entitlements →
            </button>
          </h2>
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="text-[#A0A0A0] text-[11px] uppercase tracking-wider">
                  <th className="text-left py-1.5">Email</th>
                  <th className="text-left py-1.5">Name</th>
                  <th className="text-left py-1.5">Role</th>
                  <th className="text-left py-1.5">Bot</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#252525]">
                {users.map((u) => (
                  <tr key={u.id} data-testid={`admin-user-row-${u.id}`}>
                    <td className="py-1.5">{u.email}</td>
                    <td className="py-1.5">
                      {u.first_name} {u.last_name}
                    </td>
                    <td className="py-1.5 font-mono text-xs">{u.role}</td>
                    <td className="py-1.5">{u.is_bot ? "🤖" : "-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>

        {/* E. Audit events */}
        <section
          data-testid="admin-section-audit"
          className="bg-[#1C1C1C] border border-[#252525] rounded-xl p-5"
        >
          <h2 className="font-display font-bold text-base uppercase flex items-center gap-2 mb-3">
            <ClipboardList size={14} className="text-[#FF6B00]" /> Recent Admin Actions ({auditEvents.length})
          </h2>
          <div className="overflow-x-auto max-h-[400px]">
            <table className="w-full text-sm font-body">
              <thead>
                <tr className="text-[#A0A0A0] text-[11px] uppercase tracking-wider">
                  <th className="text-left py-1.5">When</th>
                  <th className="text-left py-1.5">Admin</th>
                  <th className="text-left py-1.5">Action</th>
                  <th className="text-left py-1.5">League</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#252525]">
                {auditEvents.map((ev) => (
                  <tr key={ev.id} data-testid={`admin-audit-row-${ev.id}`}>
                    <td className="py-1.5 text-[11px] text-[#A0A0A0]">
                      {ev.created_at
                        ? new Date(ev.created_at).toLocaleString()
                        : "-"}
                    </td>
                    <td className="py-1.5">{ev.admin_email || "-"}</td>
                    <td className="py-1.5 font-mono text-xs">{ev.action_type}</td>
                    <td className="py-1.5 font-mono text-[11px] text-[#A0A0A0]">
                      {ev.league_id ? ev.league_id.slice(0, 8) : "-"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      </main>

      {/* [TD-89 v2 Verdict 13.A] Bulk-delete confirmation with typed phrase */}
      <ConfirmModal
        open={bulkConfirmOpen}
        onCancel={() => setBulkConfirmOpen(false)}
        onConfirm={_bulkDelete}
        title="Bulk Delete Leagues"
        body={
          <div>
            <p className="mb-2">The following leagues will be permanently deleted:</p>
            <ul className="max-h-40 overflow-y-auto text-xs font-mono space-y-1 mb-2 pl-4 list-disc">
              {leagues
                .filter((l) => selectedLeagueIds.has(l.id))
                .map((l) => (
                  <li key={l.id} className="text-[#F5F5F5]">{l.name}</li>
                ))}
            </ul>
            <p><strong>This cannot be undone.</strong></p>
          </div>
        }
        confirmLabel="Delete"
        destructive
        typedConfirmation={`DELETE ${selectedLeagueIds.size} LEAGUES`}
        isDark={true}
        testIdPrefix="admin-bulk-confirm"
      />

      {/* [TD-89 v2 Verdict 7.A] Legacy single-league delete wrapped in ConfirmModal */}
      <ConfirmModal
        open={!!legacyDelete}
        onCancel={() => setLegacyDelete(null)}
        onConfirm={async () => { const d = legacyDelete; setLegacyDelete(null); if (d) await _doLegacyForceDelete(d); }}
        title={`Delete ${legacyDelete?.name || ""}?`}
        body={<>This permanently deletes the league and all its data. <strong>This cannot be undone.</strong></>}
        confirmLabel="Delete"
        destructive
        isDark={true}
        testIdPrefix="admin-legacy-confirm-delete"
      />
    </div>
  );
}

const Pair = ({ label, v, ok }) => (
  <>
    <dt className="text-[#A0A0A0] uppercase tracking-wider text-[11px]">
      {label}
    </dt>
    <dd className={ok ? "text-[#00C853]" : "text-[#FFB020]"}>{v}</dd>
  </>
);
